import { useEffect, useState } from "react";

const HEARTBEAT_URL = "https://okhati.app/api/heartbeat";
const ACCEPTED_TIMEZONES = ["Asia/Kathmandu", "Asia/Katmandu"];

// Allow 2 minutes of difference between client and server.
const MAX_TIME_DRIFT_MS = 2 * 60 * 1000;

type TimeIssue = "timezone" | "clock" | null;

const useSystemTimeCheck = (): TimeIssue => {
  const [issue, setIssue] = useState<TimeIssue>(null);

  useEffect(() => {
    let cancelled = false;

    const checkSystemTime = async () => {
      // Check timezone first
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (!ACCEPTED_TIMEZONES.includes(timezone)) {
        if (!cancelled) {
          setIssue("timezone");
        }

        return;
      }

      try {
        const requestStart = Date.now();

        const response = await fetch(`${HEARTBEAT_URL}?t=${requestStart}`, {
          cache: "no-store"
        });

        const requestEnd = Date.now();

        if (!response.ok) {
          return;
        }

        const data = await response.json();

        if (!data?.date) {
          return;
        }

        const serverTime = new Date(data.date).getTime();

        if (Number.isNaN(serverTime)) {
          return;
        }

        // Client time at the approximate moment the server generated the timestamp
        const clientMidpoint = requestStart + (requestEnd - requestStart) / 2;
        const drift = Math.abs(clientMidpoint - serverTime);

        if (!cancelled) {
          setIssue(drift > MAX_TIME_DRIFT_MS ? "clock" : null);
        }
      } catch {
        //   just a commennent
      }
    };

    checkSystemTime();

    const interval = setInterval(checkSystemTime, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return issue;
};

export default useSystemTimeCheck;
